// Das Geruest des Anwendungsrahmens (ST-UX-001).
//
// Es loest drei Dinge, die eine Einzelseitenanwendung ohne Rahmen still falsch
// macht:
//
//   1. LANDMARKEN. Kopfbereich, Navigation und Inhalt sind benannte Bereiche.
//      Wer mit einem Screenreader arbeitet, springt zwischen ihnen, statt jeden
//      Bildschirm von oben zu lesen (NFR-ACC-001, WCAG 1.3.1).
//   2. DER FENSTERTITEL. Er wechselt mit dem Bildschirm. Ohne das heissen alle
//      Tabs gleich, und die Rueckkehr aus einer anderen Anwendung wird zum
//      Suchspiel (WCAG 2.4.2).
//   3. DER FOKUS. Nach einem Wechsel steht er auf dem neuen Inhalt und nicht
//      am Anfang des Dokuments. Ohne das laeuft die Tastaturbedienung nach
//      jedem Klick wieder durch die gesamte Navigation (WCAG 2.4.3).
//
// DIE GESTALT: ein Markenkopf ueber die ganze Breite mit der Suche in der
// Mitte, darunter links die Navigation. Links statt oben, weil die Anwendung
// waechst: eine Leiste mit Registern laeuft nach wenigen Bereichen ueber, eine
// Spalte mit Gruppen nicht. Die Entscheidung steht in ST-UX-001 (decisions).
//
// WAS HIER BEWUSST FEHLT: eine zusaetzliche Ansageregion fuer den
// Bildschirmwechsel. Der Fokuswechsel auf den Hauptbereich laesst dessen
// Ueberschrift ohnehin vorlesen; eine Live-Region daneben sagt dieselbe Sache
// ein zweites Mal. Und Zaehler an den Eintraegen: es gibt heute keine
// Schnittstelle, die eine Zahl liefert, und eine ganze Liste je Seitenaufruf
// zu laden, nur um sie zu zaehlen, waere ein Fehlerfall mehr im Rahmen.

import { createContext, Fragment, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import {
  Avatar,
  Body1,
  Button,
  Caption1,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemLink,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Title3,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';

import { partyHref, rememberSearchFilter, rememberSearchTerm } from '../parties.ts';
import { Befehlspalette } from './befehlspalette.tsx';
import type { Befehl } from './befehlspalette-eintraege.ts';
import { KuerzelUebersicht } from './kuerzel-uebersicht.tsx';
import {
  KOPF_FLAECHE,
  KOPF_FLAECHE_GEDRUECKT,
  KOPF_FLAECHE_HOVER,
  KOPF_HOEHE,
  KOPF_KNOPF,
  KOPF_SCHRIFT,
  KOPF_SUCHE,
  KOPF_VAR,
  NAV_BREITE,
  NAV_ZEILE,
} from './masse.ts';
import { KontextbefehleKontext, SchnellzugriffKontext, type KontextbefehleStand } from './kontextbefehle.ts';
import { istMacTastatur, PALETTE_ID_FELD, useEinzeltasten, useTastenkuerzel, type KuerzelBefehl } from './tastenkuerzel.ts';
import { navigationCurrent } from './current-area.ts';
import { AppLink, navigate, setzeAdressParameter, usePath, warUmleitung } from './router.tsx';
import { RAHMEN, useRahmenScroll } from './rahmen-scroll.ts';
import { Symbol, type SymbolName } from './symbole.tsx';
import { SCHMUCK_ATTRIBUT, useSchmuckStyles } from './schmucktext.ts';
import { Wortmarke } from './wortmarke.tsx';
import { useZurueckSchliesstVollbild } from './zurueck-schliesst-vollbild.ts';
import {
  HOME,
  NAV_GROUPS,
  SCREENS,
  documentTitleFor,
  isOffered,
  offeredScreens,
  resolve,
  type KnownRoles,
  type Screen,
} from './navigation.ts';

/**
 * Die Ueberschrift eines Bildschirms, der einen bestimmten Datensatz zeigt.
 *
 * Der Rahmen setzt die <h1> aus der Bildschirmtabelle ("Kontakt"). Ein
 * Detailbildschirm meldet ueber diesen Haken, WELCHER Datensatz es ist -- dann
 * heissen die <h1> und der Fenstertitel wie er. Vorher, beim Laden und bei
 * einem Fehler gilt der Titel der Tabelle; so gibt es genau eine <h1>.
 */
const SeitenueberschriftKontext = createContext<(ueberschrift: string | undefined) => void>(() => undefined);

export function useSeitenueberschrift(ueberschrift: string | undefined): void {
  const setzen = useContext(SeitenueberschriftKontext);
  // Vor dem Anstrich: traegt der Bildschirm seine h1 selbst (useUeberschriftImInhalt),
  // gaebe es sonst einen Moment mit der verborgenen Rahmen-h1 UND der des Inhalts.
  useLayoutEffect(() => {
    setzen(ueberschrift);
    return () => setzen(undefined);
  }, [setzen, ueberschrift]);
}

/**
 * Eine Meldung, die VOR der des Bildschirms gilt: der Zustand "nicht da" in
 * einem Bildschirm, der selbst eine Ueberschrift meldet. Layout-Effekte laufen
 * vom Kind zum Vorfahren; wechselt der Bildschirm im selben Durchlauf seine
 * eigene Meldung (etwa auf undefined), loeschte er die des Kinds, und es gaebe
 * zwei h1. Darum NACH den Layout-Effekten (useEffect) und ohne Ruecknahme beim
 * Aushaengen: die Meldung ist an den Pfad gebunden und verfaellt mit ihm, und
 * meldet der Bildschirm danach selbst wieder, gilt seine.
 */
export function useSeitenueberschriftVorrang(ueberschrift: string): void {
  const setzen = useContext(SeitenueberschriftKontext);
  useEffect(() => {
    setzen(ueberschrift);
  }, [setzen, ueberschrift]);
}

/**
 * Ein Bildschirm, der seine <h1> selbst in seiner Kopfkarte traegt (Kontaktseite).
 *
 * Er ruft den Haken beim Einhaengen auf; der Rahmen haelt seine eigene <h1> dann
 * optisch verborgen, solange der Bildschirm keinen Namen gemeldet hat (laden,
 * unbekannt, abgelehnt: so gibt es in jedem Zustand genau eine), und zeigt sie
 * gar nicht mehr, sobald er einen meldet. `useLayoutEffect`, damit der erste
 * Bildanstrich die sichtbare Rahmen-h1 nie zeigt und nichts um ihre Hoehe springt.
 */
const UeberschriftImInhaltKontext = createContext<(aktiv: boolean) => void>(() => undefined);

export function useUeberschriftImInhalt(): void {
  const setzen = useContext(UeberschriftImInhaltKontext);
  useLayoutEffect(() => {
    setzen(true);
    return () => setzen(false);
  }, [setzen]);
}

const AUSLOESER_NAME = 'Kontakt finden oder Befehl ausführen';

/** Die Bildzeichen der Navigationseinträge (v4); ein Eintrag ohne Zeichen bleibt ohne. */
const NAV_SYMBOL: Readonly<Record<string, SymbolName>> = {
  kontakte: 'kontakte',
  wiedervorlagen: 'uhr',
  sammlungen: 'lesezeichen',
  massenpflege: 'ebenen',
  stichwortkatalog: 'stichwort',
  rollenkatalog: 'rolle',
  datenqualitaet: 'schild',
  testversand: 'senden',
};

/** Ab dieser Breite steht die Navigation als Spalte; darunter hinter dem Menueknopf. */
const SCHMAL = '@media (max-width: 48rem)';
/** Unter 360 px (22,4375 rem, waechst mit dem Textzoom): das Konto nur als Kreis. */
const ENG = '@media (max-width: 22.4375rem)';
/**
 * Unter 390 px (24,3125 rem): das Konto nur als Kreis. Mit Pfeil brauchte es 26 px mehr, und der
 * Kopf brach bei 360-389 px (verbreitete Handybreiten) zweizeilig um: 104 statt 60 px Bildhoehe.
 */
const KONTO_KREIS = '@media (max-width: 24.3125rem)';
/**
 * Grosse Grundschrift am Handy: unter 17,5 rem, also bei 16 px nie (280 px), bei
 * 200 % schon bis 560 px. Dann traegt der Kopf nur das Bildzeichen der Marke und
 * Knoepfe von 44 Pixeln, sonst brach er auf 320 px dreizeilig um (264 px Hoehe).
 */
// "screen and": Griffel ordnet Medienabfragen nach ihrem Text; so steht diese
// hinter ENG und SCHMAL und gewinnt, wo beide greifen.
const GROSSE_SCHRIFT = '@media screen and (max-width: 17.5rem)';
/** Ab hier ist der Hauptbereich breiter als seine 90 rem (Navigation 15 rem daneben). */
const RAHMEN_BREIT = '@media screen and (min-width: 105rem) and (min-height: 30.0625rem)';
const RAHMEN_OHNE_NAV_BREIT = '@media screen and (min-width: 90rem) and (min-height: 30.0625rem)';

/** Initialen wie im Fluent-Avatar: erster Buchstabe des ersten und des letzten Worts. */
function initialenVon(name: string): string {
  const woerter = name.split(/\s+/).filter((wort) => /\p{L}/u.test(wort));
  const erstes = woerter[0];
  const letztes = woerter.length > 1 ? woerter[woerter.length - 1] : undefined;
  return [erstes, letztes].map((wort) => (wort === undefined ? '' : (/\p{L}/u.exec(wort)?.[0] ?? ''))).join('').toUpperCase();
}

const useStyles = makeStyles({
  shell: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    // Papier als unterste Flaeche (ADR-0033). Ohne eine Flaeche UNTER der
    // Karte schwebt die Karte auf demselben Weiss.
    backgroundColor: tokens.colorNeutralBackground3,
    // Fester Rahmen (rahmen-scroll.ts): genau ein Fenster hoch, gescrollt wird darin.
    [RAHMEN]: { height: '100dvh', minHeight: 0, overflow: 'hidden' },
  },
  kopf: {
    // Eigene Farben des Kopfes (masse.ts): Weiss darauf ueber 4,5:1 in Ruhe,
    // Hover und gedrueckt, hell und dunkel. Die Variablen erben die Kinder.
    '--liaison-kopf-flaeche': KOPF_FLAECHE.hell,
    '--liaison-kopf-hover': KOPF_FLAECHE_HOVER.hell,
    '--liaison-kopf-gedrueckt': KOPF_FLAECHE_GEDRUECKT.hell,
    '--liaison-kopf-schrift': KOPF_SCHRIFT.hell,
    '@media (prefers-color-scheme: dark)': {
      '--liaison-kopf-flaeche': KOPF_FLAECHE.dunkel,
      '--liaison-kopf-hover': KOPF_FLAECHE_HOVER.dunkel,
      '--liaison-kopf-gedrueckt': KOPF_FLAECHE_GEDRUECKT.dunkel,
      '--liaison-kopf-schrift': KOPF_SCHRIFT.dunkel,
    },
    backgroundColor: KOPF_VAR.flaeche,
    color: KOPF_VAR.schrift,
    display: 'flex',
    // Umbruch erlaubt: bei 200 % Textzoom auf 320 Pixeln passt die Wortmarke
    // nicht neben Menue und Konto (WCAG 1.4.10). Bei normaler Schrift bleibt
    // es eine Zeile.
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.75rem 1.25rem',
    padding: '0.5rem 1.5rem',
    minHeight: KOPF_HOEHE,
    flexShrink: 0,
    '@media print': { display: 'none' },
    // Zwei getrennte Bereiche statt zweier sich ueberdeckender Abfragen: Griffel
    // ordnet Medienabfragen selbst, und SCHMAL gewann sonst auch unter 360 px.
    '@media (min-width: 22.5rem) and (max-width: 48rem)': { padding: '0.375rem 1rem', gap: '0.5rem' },
    // Unter 360 px: engere Abstaende, damit Menue, Marke, Lupe, "+" und Konto in
    // eine Zeile passen (DESIGN zu #551; 351 px bei 8 px Abstand).
    // 8 px Rand statt 12: sonst blieben der Lupe bei 320 px nur 38 px statt 44 (Muster B4).
    [ENG]: { padding: '0.375rem 0.5rem', gap: '0.25rem' },
    [GROSSE_SCHRIFT]: { padding: '6px 8px', gap: '4px', flexWrap: 'nowrap', minHeight: '56px' },
  },
  marke: {
    display: 'inline-flex',
    alignItems: 'center',
    color: 'inherit',
    textDecorationLine: 'none',
    flexShrink: 0,
    // Die Wortmarke waechst mit dem Textzoom mit: Hoehe in rem statt in Pixeln.
    // docs/brand/README.md nennt 24 Pixel als Mindestgroesse; 1.625rem sind 26.
    '& svg': { height: '1.625rem', width: 'auto', display: 'block' },
    // Unter 360 px 24 px hoch, die Mindestgroesse aus docs/brand/README.md.
    [ENG]: { '& svg': { height: '1.5rem' } },
    // Nur das Bildzeichen: es steht links im SVG und ist so breit wie hoch.
    // 44 px Ziel wie die Knoepfe daneben; der Rand von 6 px zeigt noch nichts vom "L".
    [GROSSE_SCHRIFT]: {
      boxSizing: 'border-box',
      width: '44px',
      minHeight: '44px',
      paddingLeft: '6px',
      overflow: 'hidden',
      '& svg': { height: '32px', width: '147px', maxWidth: 'none', flexShrink: 0 },
    },
    ':focus-visible': { outline: `2px solid ${KOPF_VAR.schrift}`, outlineOffset: '2px' },
  },
  suche: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    minWidth: 0,
    [SCHMAL]: { display: 'none' },
  },
  // Der Ausloeser der Befehlspalette: ein Knopf, kein Feld; getippt wird in der
  // Palette. Durchscheinend auf dem Blau wie v4 und damit KEINE Hauptaktion
  // (Kopf ohne primary, Michael 06.10.2026). Werte und Kontrast: masse.ts.
  ausloeser: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    width: '100%',
    maxWidth: '32.5rem',
    minWidth: 0,
    boxSizing: 'border-box',
    minHeight: '2.25rem',
    padding: '0.375rem 0.625rem',
    ...shorthands.border('1px', 'solid', KOPF_SUCHE.rand),
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: KOPF_SUCHE.flaeche,
    color: KOPF_VAR.schrift,
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    ':hover': { ...shorthands.borderColor(KOPF_VAR.schrift) },
    ':focus-visible': { outline: `2px solid ${KOPF_VAR.schrift}`, outlineOffset: '2px' },
  },
  ausloeserText: { flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tasten: { display: 'inline-flex', gap: '0.1875rem', flexShrink: 0 },
  taste: {
    // Markenschrift statt Festbreite (v4): das Zeichen ist ein Hinweis, kein Code.
    fontFamily: 'inherit',
    fontSize: '0.71875rem', // v4 11,5 px
    padding: '0 0.3125rem',
    ...shorthands.border('1px', 'solid', KOPF_SUCHE.rand),
    borderBottomWidth: '2px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: 'transparent',
    color: KOPF_VAR.schrift,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  aufBlau: {
    // Knoepfe auf Markenblau: Weiss als Schrift, die Flaechen aus der Rampe.
    // Ohne Fluents Mindestbreite von 96 Pixeln, sonst bricht der Kopf bei
    // 390 Pixeln um.
    minWidth: 'auto',
    color: KOPF_VAR.schrift,
    backgroundColor: 'transparent',
    ...shorthands.borderColor('transparent'),
    ':hover': { color: KOPF_VAR.schrift, backgroundColor: KOPF_VAR.hover },
    ':hover:active': { color: KOPF_VAR.schrift, backgroundColor: KOPF_VAR.gedrueckt },
    ':focus-visible': { outline: `2px solid ${KOPF_VAR.schrift}`, outlineOffset: '1px' },
  },
  anlegen: {
    // Die Hauptaktion des Kopfes: Weiss auf Blau umgekehrt, damit sie sich vom
    // Rest des Kopfes abhebt.
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    flexShrink: 0,
    minHeight: '2rem',
    padding: '0.3125rem 0.75rem',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: KOPF_KNOPF.flaeche,
    color: KOPF_KNOPF.schrift,
    fontWeight: tokens.fontWeightSemibold,
    textDecorationLine: 'none',
    ':hover': { backgroundColor: KOPF_KNOPF.hover, color: KOPF_KNOPF.schrift },
    ':focus-visible': { outline: `2px solid ${KOPF_VAR.schrift}`, outlineOffset: '2px' },
    [SCHMAL]: { display: 'none' },
  },
  // Schmal steht die Hauptaktion als Symbolknopf im Kopf (v4); Name und
  // sichtbarer Tooltip "Kontakt anlegen".
  anlegenSymbol: {
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    // 44 px Fingerziel schmal (M-04, WCAG 2.5.5).
    minWidth: '2.75rem',
    minHeight: '2.75rem',
    [GROSSE_SCHRIFT]: { minWidth: '44px', minHeight: '44px' },
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: KOPF_KNOPF.flaeche,
    color: KOPF_KNOPF.schrift,
    ':hover': { backgroundColor: KOPF_KNOPF.hover, color: KOPF_KNOPF.schrift },
    ':focus-visible': { outline: `2px solid ${KOPF_VAR.schrift}`, outlineOffset: '2px' },
    [SCHMAL]: { display: 'inline-flex' },
  },
  // Auf "Kontakt anlegen" selbst ist der Kopfverweis der aktuelle Ort und kein
  // Knopf: sonst staenden zwei Hauptaktionen auf der Seite (NFR-UX-004), die
  // eine davon fuehrte nur zu sich selbst. Wie der aktuelle Eintrag der
  // Navigation mit Balken und Schriftgewicht, nicht nur ueber Farbe (WCAG 1.4.1).
  anlegenAktuell: {
    backgroundColor: 'transparent',
    color: KOPF_VAR.schrift,
    fontWeight: tokens.fontWeightSemibold,
    boxShadow: `inset 0 -2px 0 ${KOPF_VAR.schrift}`,
    ':hover': { backgroundColor: KOPF_VAR.hover, color: KOPF_VAR.schrift },
  },
  schmalNur: { display: 'none', [SCHMAL]: { display: 'inline-flex' } },
  // 44 px wie "+" daneben: beide sind schmal die Fingerziele des Kopfs (M-04).
  menueSymbol: { minWidth: '2.75rem', minHeight: '2.75rem', padding: '0.5rem', [GROSSE_SCHRIFT]: { minWidth: '44px', minHeight: '44px', padding: 0 } },
  // Schmal dieselbe durchscheinende Flaeche wie breit, nur mit der Lupe (v4):
  // sie fuellt den freien Platz und bleibt als Suche erkennbar.
  schmalSuche: {
    display: 'none',
    alignItems: 'center',
    flexGrow: 1,
    // Basis 0: der Kopf bricht um (flex-wrap), wenn die Inhaltsbreite nicht passt --
    // der Platzhalter fuellt nur, was uebrig ist, und wird sonst gekuerzt.
    flexBasis: 0,
    overflow: 'hidden',
    // Die Lupe misst sich selbst: der Platzhalter erscheint erst, wenn er ganz passt.
    containerType: 'inline-size',
    containerName: 'kopfsuche',
    minWidth: '2.25rem',
    // 44 px hoch wie Menue und "+" (Mobilregel B4, DESIGN-P3 zu #551); die Breite fuellt den Rest.
    minHeight: '2.75rem',
    [GROSSE_SCHRIFT]: { minWidth: '44px', minHeight: '44px', padding: '0 10px' },
    boxSizing: 'border-box',
    color: KOPF_VAR.schrift,
    backgroundColor: KOPF_SUCHE.flaeche,
    padding: '0.375rem 0.5rem',
    ...shorthands.border('1px', 'solid', KOPF_SUCHE.rand),
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    ':hover': { ...shorthands.borderColor(KOPF_VAR.schrift) },
    ':focus-visible': { outline: `2px solid ${KOPF_VAR.schrift}`, outlineOffset: '1px' },
    [SCHMAL]: { display: 'inline-flex' },
  },
  // Nur ganz oder gar nicht (Codex-Cloud-P2 #562): ein abgeschnittenes
  // "Kontakt fi…" waere ein falscher Platzhalter. Die Lupe ist ihr eigener
  // Container; ab 8 rem Inhaltsbreite (Symbol, Abstaende und der Text bei 100 %, mit
  // dem Textzoom wachsend) steht der Text, darunter nur die Lupe.
  schmalSucheText: {
    display: 'none',
    marginInlineStart: '0.375rem',
    fontSize: tokens.fontSizeBase300,
    whiteSpace: 'nowrap',
    '@container kopfsuche (min-width: 8rem)': { display: 'inline' },
  },
  konto: { flexShrink: 0, marginLeft: 'auto', [SCHMAL]: { marginLeft: 0 } },
  // Unter 390 px passt der Kopf sonst nicht in eine Zeile (DESIGN zu #551): das
  // Konto ist dann nur der Kreis, als 44-px-Fingerziel, ohne Pfeil. Der Name
  // steht weiter im Label und im geoeffneten Menue.
  // Immer 44 px hoch wie Menue, Lupe und "+"; schmal zusaetzlich 44 px breit ohne Innenabstand.
  kontoKnopf: {
    minHeight: '2.75rem',
    [KONTO_KREIS]: { minWidth: '2.75rem', padding: 0 },
    [GROSSE_SCHRIFT]: { minWidth: '44px', minHeight: '44px' },
  },
  kontoPfeil: { [KONTO_KREIS]: { display: 'none' } },
  // Weisses Bildzeichen mit dunkler Schrift auf dem Blau (v4), hell und dunkel gleich.
  kontoBild: { backgroundColor: KOPF_KNOPF.flaeche, color: KOPF_KNOPF.schrift },
  kontoName: {
    // 8 px zwischen Bildzeichen und Name (v4; vorher klebten beide aneinander).
    marginInlineStart: '0.5rem',
    // Nur auf breiten Flaechen. Auf schmalen genuegt das Bildzeichen; der Name
    // steht dann im geoeffneten Menue.
    [SCHMAL]: { display: 'none' },
  },
  kontoZeile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
    padding: '0.5rem 0.75rem',
  },
  rumpf: {
    display: 'flex',
    flexGrow: 1,
    minWidth: 0,
    // Bezug der schmalen Navigation, die sich ueber den Inhalt legt.
    position: 'relative',
    [SCHMAL]: { flexDirection: 'column' },
    [RAHMEN]: { minHeight: 0 },
  },
  nav: {
    width: NAV_BREITE,
    flexShrink: 0,
    boxSizing: 'border-box',
    // Die Spalte reicht ueber die volle Hoehe; mit der Seite laeuft nur ihr
    // Inhalt mit (navInhalt).
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    // Im Rahmen scrollt die Spalte bei Ueberlaenge selbst (200 % Text).
    [RAHMEN]: { overflowY: 'auto', overscrollBehavior: 'contain' },
    '@media print': { display: 'none' },
    // Schmal legt sie sich unter dem Kopf ueber den Inhalt (v4), statt ihn
    // nach unten zu schieben: wer das Menue schliesst, steht wieder dort, wo
    // er war. Sie bleibt dieselbe Landmarke.
    [SCHMAL]: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 10,
      width: 'auto',
      // Die WIRKLICHE Kopfhoehe (gemessen, siehe kopfRef): bei 200 % Textzoom
      // bricht der Kopf um, und mit der Mindesthoehe allein laege das Ende der
      // Navigation unter dem Bildrand.
      maxHeight: `calc(100dvh - var(--liaison-kopf-gemessen, ${KOPF_HOEHE}))`,
      overflowY: 'auto',
      borderRight: 'none',
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
      boxShadow: tokens.shadow16,
    },
  },
  navInhalt: {
    padding: '1rem 0.75rem',
    position: 'sticky',
    top: 0,
    maxHeight: '100vh',
    boxSizing: 'border-box',
    // Bei 200 % Textzoom wird die Spalte hoeher als das Fenster; sie scrollt
    // dann selbst, statt Eintraege abzuschneiden (WCAG 1.4.10).
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
    [SCHMAL]: { position: 'static', maxHeight: 'none', padding: '0.75rem 1rem' },
    [RAHMEN]: { position: 'static', maxHeight: 'none', overflowY: 'visible' },
  },
  navZu: { [SCHMAL]: { display: 'none' } },
  // Schmal dunkelt eine Flaeche den Inhalt unter dem offenen Menue ab (M-04);
  // ein Klick darauf schliesst es. Breit gibt es sie nicht.
  scrim: {
    display: 'none',
    [SCHMAL]: {
      display: 'block',
      position: 'absolute',
      inset: 0,
      zIndex: 9,
      backgroundColor: tokens.colorBackgroundOverlay,
    },
  },
  // Schliessen-X oben rechts im schmalen Menue (M-04).
  schliessenZeile: { display: 'none', [SCHMAL]: { display: 'flex', justifyContent: 'flex-end', marginBottom: '-0.75rem' } },
  schliessenKnopf: { minWidth: '2.75rem', minHeight: '2.75rem' },
  gruppe: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    // Schmal setzt eine Linie Versand und Datenpflege von den Arbeitsbereichen ab (M-04).
    [SCHMAL]: { '& + &': { borderTop: `1px solid ${tokens.colorNeutralStroke2}`, paddingTop: '0.75rem' } },
  },
  gruppeTitel: {
    margin: '0 0.75rem 0.25rem',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground3,
  },
  liste: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.125rem' },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    minHeight: NAV_ZEILE,
    boxSizing: 'border-box',
    padding: '0.5rem 0.625rem',
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground2,
    textDecorationLine: 'none',
    ':hover': { backgroundColor: tokens.colorNeutralBackground4, color: tokens.colorNeutralForeground1 },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '1px' },
    // Der aktuelle Eintrag traegt Flaeche, Schriftgewicht und einen Balken,
    // nicht nur Farbe (ADR-0002: Status nie nur ueber Farbe; WCAG 1.4.1).
    // "page" ist der Bildschirm selbst, "true" der Bereich einer Unterseite.
    '&[aria-current="page"], &[aria-current="true"]': {
      backgroundColor: tokens.colorBrandBackground2,
      color: tokens.colorNeutralForeground1,
      fontWeight: tokens.fontWeightSemibold,
      boxShadow: `inset 3px 0 0 ${tokens.colorBrandStroke1}`,
    },
  },
  ueberschrift: { overflowWrap: 'anywhere' },
  // Optisch verborgen, fuer Hilfsmittel da; aus dem Fluss, damit kein Abstand bleibt.
  nurLeser: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    minWidth: 0,
    width: '100%',
    // DIE ARBEITSFLAECHE NIMMT DEN PLATZ. Die Obergrenze verhindert nur, dass
    // Zeilen auf sehr breiten Bildschirmen ins Unlesbare laufen.
    maxWidth: '90rem',
    boxSizing: 'border-box',
    padding: '1.5rem 1.5rem 3rem',
    flexGrow: 1,
    [SCHMAL]: { padding: '1rem 1rem 3rem' },
    // Im Rahmen der einzige Scrollbereich. Sehr breit reicht er bis an den
    // rechten Fensterrand, damit das Mausrad auch neben dem Inhalt greift; die
    // Zeilenlaenge begrenzt dann der rechte Innenabstand statt maxWidth.
    // Feste Hoehe macht aus der Flex-Spalte einen Schrumpfraum: ein Kind mit
    // eigenem overflow (etwa eine waagerecht scrollende Tabelle) fiele sonst
    // auf seine Mindesthoehe 0 zusammen.
    // Stabile Rinne: der Inhalt springt nicht, wenn eine Seite lang genug zum
    // Scrollen wird. Die Knopfreihe der Kontaktkarte bleibt bei 1280 px mit
    // Balken einzeilig (gemessen).
    [RAHMEN]: { overflowY: 'auto', overscrollBehavior: 'contain', scrollbarGutter: 'stable', minHeight: 0, '& > *': { flexShrink: 0 } },
    [RAHMEN_BREIT]: { maxWidth: 'none', paddingRight: `calc(100% - ${NAV_BREITE} - 90rem + 1.5rem)` },
    // Der Fokusrahmen des Hauptbereichs wird unterdrueckt: der Fokus landet
    // hier programmatisch nach einem Wechsel, nicht durch eine Tastenhandlung.
    ':focus-visible': { outlineStyle: 'none' },
  },
  mainOhneNav: {
    [RAHMEN_OHNE_NAV_BREIT]: { maxWidth: 'none', paddingRight: 'calc(100% - 90rem + 1.5rem)' },
  },
});

export function AppShell({
  screen,
  roles,
  accountName,
  accountLogin,
  onSignOut,
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  children,
}: {
  /** `undefined` heisst: diese Adresse gibt es nicht. */
  readonly screen: Screen | undefined;
  readonly roles: KnownRoles;
  readonly accountName: string | undefined;
  readonly accountLogin: string | undefined;
  readonly onSignOut: () => void;
  /** Fuer die Befehlspalette: dieselben drei Dinge wie fuer jeden Lesebildschirm. */
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly children: ReactNode;
}): ReactNode {
  const styles = useStyles();
  const schmuck = useSchmuckStyles();
  const initialen = initialenVon(accountName ?? '');
  const main = useRef<HTMLElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const kopfRef = useRef<HTMLElement>(null);
  useRahmenScroll(main);
  // Die Hoehe des Kopfes als Variable fuer die schmale Navigation; sie folgt
  // Umbruch und Textzoom, ohne dass der Rahmen neu zeichnet.
  useEffect(() => {
    const kopf = kopfRef.current;
    const shell = shellRef.current;
    if (kopf === null || shell === null || typeof ResizeObserver === 'undefined') return undefined;
    const setzen = (): void => shell.style.setProperty('--liaison-kopf-gemessen', `${kopf.getBoundingClientRect().height}px`);
    setzen();
    const beobachter = new ResizeObserver(setzen);
    beobachter.observe(kopf);
    return () => beobachter.disconnect();
  }, []);
  // Der Fokus haengt an der ADRESSE und nicht am Bildschirmobjekt. Waehrend
  // der Anmeldung wechselt das Bildschirmobjekt (Pruefung -> Anmelden ->
  // Zugang), ohne dass die Nutzerin irgendwohin navigiert waere; an das Objekt
  // gebunden, spraenge der Fokus dabei ungefragt in den Hauptbereich und
  // uebersprange den Skiplink.
  const path = usePath();
  const [menueOffen, setMenueOffen] = useState(false);
  const [paletteOffen, setPaletteOffen] = useState(false);
  const [uebersichtOffen, setUebersichtOffen] = useState(false);
  const [einzeltasten, setEinzeltasten] = useEinzeltasten();
  const [kontext, setKontext] = useState<KontextbefehleStand>({});
  const meldeKontext = useCallback((id: 'notiz', befehl: KontextbefehleStand['notiz']) => {
    setKontext((alt) => {
      const neu = { ...alt };
      if (befehl === undefined) {
        delete neu[id];
      } else {
        neu[id] = befehl;
      }
      return neu;
    });
  }, []);
  // Wer vor Palette oder Uebersicht den Fokus hatte, bekommt ihn zurueck. Die
  // Palette geht auch per Taste auf; Fluents Ruecksprung kennt nur den Ausloeser.
  const vorherFokus = useRef<HTMLElement | null>(null);
  // Die Rueckgabe wird erst NACH dem Aushaengen des Dialogs geplant. Tabster
  // macht seine Wurzelplatzhalter beim Verlassen per Zeitgeber tabbar; kaeme
  // der Fokus davor zurueck, blieben sie tabbar unter aria-hidden (axe
  // aria-hidden-focus, A96). Lag der Fokus vorher auf body, geht er in den
  // Hauptbereich: body ist kein Ziel, und Tabster saehe kein Betreten. Ebenso,
  // wenn das Ziel inzwischen fehlt. Zwei Rueckgaben kurz nacheinander: die letzte gilt.
  const rueckgabeZiel = useRef<HTMLElement | null>(null);
  const [rueckgabe, setRueckgabe] = useState(0);
  useEffect(() => {
    if (rueckgabe === 0) return undefined;
    const ziel = rueckgabeZiel.current;
    rueckgabeZiel.current = null;
    const zeitgeber = window.setTimeout(() => {
      if (ziel !== null && ziel !== document.body && ziel.isConnected) ziel.focus();
      else main.current?.focus();
    }, 0);
    return () => window.clearTimeout(zeitgeber);
  }, [rueckgabe]);
  // Zaehlt die Suchen aus dem Kopf, solange man schon auf den Kontakten ist:
  // dort bleibt die Adresse dieselbe, und erst ein neuer Aufbau laesst den
  // Bildschirm den gemerkten Begriff uebernehmen (screens/kontakte.tsx liest
  // ihn beim Einhaengen).
  const [suchlauf, setSuchlauf] = useState(0);
  // Die gemeldete Ueberschrift gilt nur fuer die Adresse, auf der sie gemeldet
  // wurde. Nach einem Wechsel (auch von /kontakte/A nach /kontakte/B) gilt sie
  // schon im ersten Render nicht mehr -- vor dem Fokuswechsel in den
  // Hauptbereich, sonst hoerte man dort noch den Namen von A.
  const [gemeldet, setGemeldet] = useState<{ readonly pfad: string; readonly text: string } | undefined>(undefined);
  const melden = useCallback((text: string | undefined) => {
    setGemeldet(text === undefined ? undefined : { pfad: window.location.pathname, text });
  }, []);
  const eigeneUeberschrift = gemeldet !== undefined && gemeldet.pfad === path ? gemeldet.text : undefined;
  const [ueberschriftImInhalt, setUeberschriftImInhalt] = useState(false);

  useEffect(() => {
    document.title = eigeneUeberschrift === undefined ? documentTitleFor(screen) : `${eigeneUeberschrift} – LIAISON`;
  }, [screen, eigeneUeberschrift]);

  // Der zuletzt gesehene Pfad, vorbelegt mit dem aktuellen. Ein WECHSEL ist
  // damit definiert als "der Pfad ist ein anderer als beim letzten Lauf" -- und
  // nicht als "das ist nicht der erste Lauf". React fuehrt Effekte im
  // StrictMode zweimal aus; ein Wahrheitswert "schon gerendert" hielte den
  // zweiten Lauf fuer einen Wechsel und setzte den Fokus beim LADEN in den
  // Hauptbereich, am Skiplink vorbei. Ein Pfadvergleich ist idempotent.
  const letzterPfad = useRef(path);

  useEffect(() => {
    if (letzterPfad.current === path) {
      return;
    }
    letzterPfad.current = path;
    setMenueOffen(false);
    // Eine Umleitung (/kontakte/alle -> /kontakte) ist kein Wechsel: beim
    // Direktaufruf bliebe der Skiplink sonst nicht erster Halt. Kam ein Wechsel
    // davor, steht der Fokus schon im Hauptbereich.
    if (warUmleitung(path)) return;
    main.current?.focus();
  }, [path]);

  const angemeldet = accountName !== undefined;
  const screens = offeredScreens(roles);
  const anlegen = SCREENS.find((entry) => entry.id === 'kontakt-anlegen');
  const anlegenAngeboten = angemeldet && anlegen !== undefined && isOffered(anlegen, roles);
  // Ueber resolve wie der Router: auch /kontakt-anlegen/ zeigt diesen Bildschirm.
  const anlegenAktuell = resolve(path)?.screen.id === 'kontakt-anlegen';

  const darfKontakte = angemeldet && screens.some((entry) => entry.id === HOME.id);
  const darfWiedervorlagen = angemeldet && screens.some((entry) => entry.id === 'wiedervorlagen');
  const paletteVerfuegbar = angemeldet;

  const merkeFokus = (): void => {
    vorherFokus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  };
  const gibFokusZurueck = (): void => {
    rueckgabeZiel.current = vorherFokus.current;
    vorherFokus.current = null;
    setRueckgabe((zahl) => zahl + 1);
  };
  // Am Handy ist die Palette ein Vollbild: die Zurueck-Geste schliesst sie und
  // gibt den Fokus zurueck, statt die Seite zu verlassen.
  const paletteGeste = useZurueckSchliesstVollbild(() => {
    setPaletteOffen(false);
    gibFokusZurueck();
  });
  const paletteOeffnen = (): void => {
    merkeFokus();
    paletteGeste.eintragen();
    // Die schmale Navigation liegt ueber dem Inhalt; fuehrt die Palette auf
    // dieselbe Adresse, bliebe sie sonst offen und verdeckte das Ziel. Stand
    // der Fokus in ihr, geht er nach der Palette an den Menueknopf zurueck:
    // der Eintrag ist dann ausgeblendet.
    if (menueOffen && vorherFokus.current?.closest('#navigation') !== null && vorherFokus.current !== null) {
      vorherFokus.current = document.getElementById('menueknopf');
    }
    setMenueOffen(false);
    setPaletteOffen(true);
  };
  const paletteSchliessen = (): void => {
    setPaletteOffen(false);
    gibFokusZurueck();
    paletteGeste.zuruecknehmen();
  };
  const uebersichtOeffnen = (): void => {
    if (!uebersichtOffen && !paletteOffen) {
      merkeFokus();
    }
    setUebersichtOffen(true);
  };
  const uebersichtSchliessen = (): void => {
    setUebersichtOffen(false);
    gibFokusZurueck();
  };
  // Ein Wechsel setzt den Fokus selbst in den Hauptbereich (Effekt oben); ist
  // das Ziel die Seite, auf der man steht, tut es dieser Aufruf.
  const geheZu = (ziel: string): void => {
    vorherFokus.current = null;
    if (ziel === path) {
      main.current?.focus();
    } else {
      navigate(ziel);
    }
  };

  // Erst den Eintrag der Palette zuruecknehmen, dann den Befehl: ein Kontakt
  // folgt so auf die Liste, und Zurueck fuehrt vom Kontakt dorthin statt in
  // eine leere Palette.
  const befehlAusfuehren = (befehl: Befehl): void => {
    setPaletteOffen(false);
    paletteGeste.zuruecknehmen(() => befehlNachPalette(befehl));
  };
  const befehlNachPalette = (befehl: Befehl): void => {
    if (befehl.art === 'kontakt') {
      geheZu(partyHref(befehl.partyId));
    } else if (befehl.art === 'alle-treffer') {
      // Wie die Kopfsuche bis Slice 3: der Begriff bleibt im fluechtigen
      // Anwendungszustand und nie in der Adresse (ADR-0049), und eine neue
      // Suche beginnt ohne die Filter einer frueheren -- die Palette sagt das
      // an ("ohne Filter").
      rememberSearchTerm(befehl.begriff);
      rememberSearchFilter(undefined);
      vorherFokus.current = null;
      if (screen?.id === HOME.id) {
        // Treffer stehen im Register "Alle Kontakte" (L13-01).
        setzeAdressParameter('ansicht', undefined, true);
        setSuchlauf((stand) => stand + 1);
        main.current?.focus();
      } else {
        navigate(HOME.path);
      }
    } else if (befehl.art === 'aktion') {
      switch (befehl.aktion) {
        case 'anlegen':
          geheZu('/kontakt-anlegen');
          break;
        case 'kontakte':
          geheZu(HOME.path);
          break;
        case 'wiedervorlagen':
          geheZu('/wiedervorlagen');
          break;
        case 'kuerzel':
          setUebersichtOffen(true);
          break;
        case 'notiz':
          vorherFokus.current = null;
          kontext.notiz?.ausfuehren();
          break;
      }
    }
  };

  // Das Menue gibt den Fokus an seinen Knopf zurueck; dorthin soll auch die
  // Uebersicht ihn zurueckgeben.
  const uebersichtAusMenue = (): void => {
    vorherFokus.current = document.querySelector<HTMLElement>('button[aria-label^="Konto von"]');
    setUebersichtOffen(true);
  };

  const kuerzelAusfuehren = (befehl: KuerzelBefehl): void => {
    switch (befehl) {
      case 'palette':
        paletteOeffnen();
        break;
      case 'palette-fokus':
        document.getElementById(PALETTE_ID_FELD)?.focus();
        break;
      case 'anlegen':
        geheZu('/kontakt-anlegen');
        break;
      case 'notiz':
        kontext.notiz?.ausfuehren();
        break;
      case 'kontakte':
        if (darfKontakte) {
          geheZu(HOME.path);
        }
        break;
      case 'wiedervorlagen':
        if (darfWiedervorlagen) {
          geheZu('/wiedervorlagen');
        }
        break;
      case 'uebersicht':
        uebersichtOeffnen();
        break;
    }
  };

  useTastenkuerzel(
    {
      verfuegbar: paletteVerfuegbar,
      einzeltasten,
      paletteOffen,
      darfAnlegen: anlegenAngeboten,
      notizAngemeldet: kontext.notiz !== undefined,
    },
    kuerzelAusfuehren,
  );
  const mac = istMacTastatur();

  // Wie AppLink fuer Verweise, die Fluent oder der Tooltip selbst rendern:
  // Strg/Cmd, Umschalt, Alt und Mittelklick bleiben dem Browser.
  const folgeVerweis = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    const ziel = event.currentTarget.getAttribute('href');
    if (ziel !== null) {
      navigate(ziel);
    }
  };

  // Schmal liegt die Navigation UEBER dem Inhalt. Darum geht der Fokus beim
  // Oeffnen auf den ersten Eintrag (sonst laege der ganze Kopf dazwischen), und
  // verlaesst er die Navigation, schliesst sie -- er stuende sonst auf
  // Elementen, die sie verdeckt (WCAG 2.4.3, 2.4.11).
  const zuletztOffen = useRef(false);
  useEffect(() => {
    if (menueOffen && !zuletztOffen.current) {
      document.querySelector<HTMLElement>('#navigation a')?.focus();
    }
    zuletztOffen.current = menueOffen;
  }, [menueOffen]);
  const navVerlassen = (event: FocusEvent<HTMLElement>): void => {
    const ziel = event.relatedTarget;
    // Ohne Ziel bleibt es offen: Safari fokussiert einen angetippten Link nicht,
    // der Fokus faellt beim Tippen auf body. Schloesse das Menue hier, ginge der
    // Tipp ins Leere. Kein Ziel heisst auch: kein verdecktes Element im Fokus.
    if (!menueOffen || ziel === null || (ziel instanceof Node && event.currentTarget.contains(ziel))) {
      return;
    }
    if (ziel instanceof HTMLElement && ziel.id === 'menueknopf') {
      return;
    }
    setMenueOffen(false);
  };

  const navTaste = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Escape' && menueOffen) {
      setMenueOffen(false);
      document.getElementById('menueknopf')?.focus();
    }
  };

  return (
    <div className={styles.shell} ref={shellRef}>
      <header className={styles.kopf} ref={kopfRef}>
        {angemeldet && screens.length > 0 ? (
          <Button
            id="menueknopf"
            appearance="subtle"
            aria-label="Menü"
            className={mergeClasses(styles.aufBlau, styles.schmalNur, styles.menueSymbol)}
            aria-expanded={menueOffen}
            aria-controls="navigation"
            onClick={() => setMenueOffen((offen) => !offen)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && menueOffen) {
                setMenueOffen(false);
              }
            }}
          >
            <Symbol name="menue" groesse="1.25rem" />
          </Button>
        ) : undefined}

        {/* DIE ECHTE WORTMARKE (ADR-0033) in der Negativfassung fuer Markenblau.
            Das Markenhandbuch verbietet den Nachbau der Bildmarke; verwendet
            wird die freigegebene Pfadfassung. */}
        <AppLink to="/" className={styles.marke}>
          <Wortmarke negativ />
        </AppLink>

        {darfKontakte ? (
          <>
            <div className={styles.suche}>
              {/* Ein Knopf statt eines Feldes: getippt wird in der Palette. Der
                  Name ist der sichtbare Text (WCAG 2.5.3), OHNE das Wort
                  "suchen" -- die Kontaktseite hat ihren eigenen "Suchen"-Knopf,
                  und beide muessen unterscheidbar bleiben. */}
              <button
                type="button"
                className={styles.ausloeser}
                aria-label={AUSLOESER_NAME}
                aria-keyshortcuts="Control+K Meta+K"
                onClick={paletteOeffnen}
              >
                <Symbol name="suche" />
                <span className={styles.ausloeserText}>{AUSLOESER_NAME}</span>
                <span className={styles.tasten} aria-hidden="true">
                  <kbd className={mergeClasses(styles.taste, schmuck.schmuck)} {...{ [SCHMUCK_ATTRIBUT]: mac ? '⌘' : 'Strg' }} />
                  <kbd className={mergeClasses(styles.taste, schmuck.schmuck)} {...{ [SCHMUCK_ATTRIBUT]: 'K' }} />
                </span>
              </button>
            </div>
            {/* Schmal ist kein Platz fuer den Ausloeser; die Lupe oeffnet
                dieselbe Palette. Breit ist sie ausgeblendet und damit nicht im
                Barrierebaum -- es gibt nie zwei Knoepfe zugleich. */}
            <button type="button" className={styles.schmalSuche} aria-label="Kontakt finden" title="Kontakt finden" onClick={paletteOeffnen}>
              <Symbol name="suche" groesse="1.25rem" />
              {/* Ab 360 px als Platzhalter sichtbar (M-03), darunter nur die Lupe.
                  Derselbe Wortlaut wie der Name (WCAG 2.5.3). */}
              <span className={styles.schmalSucheText}>Kontakt finden</span>
            </button>
          </>
        ) : undefined}

        {anlegenAngeboten ? (
          <>
            <AppLink to="/kontakt-anlegen" className={mergeClasses(styles.anlegen, anlegenAktuell && styles.anlegenAktuell)} ariaCurrent={anlegenAktuell ? 'page' : undefined}>
              <Symbol name="plus" groesse="1rem" />
              Kontakt anlegen
            </AppLink>
            {/* Dieselbe Aktion schmal als Symbol. Breit ausgeblendet und damit
                nicht im Barrierebaum -- es gibt nie zwei Verweise zugleich. */}
            <Tooltip content="Kontakt anlegen" relationship="label" withArrow>
              <a
                href="/kontakt-anlegen"
                className={mergeClasses(styles.anlegenSymbol, anlegenAktuell && styles.anlegenAktuell)}
                aria-current={anlegenAktuell ? 'page' : undefined}
                onClick={folgeVerweis}
              >
                <Symbol name="plus" groesse="1.25rem" />
              </a>
            </Tooltip>
          </>
        ) : undefined}

        {/* DAS KONTO ALS MENUE und nicht als Knopf: Abmelden ist selten und nie
            versehentlich gewollt, und "Dein Zugang" ist eine Auskunft, keine
            Arbeit. Fluents Menu bringt Tastaturbedienung, Fokusruecksprung und
            die ARIA-Rollen mit (ADR-0002). */}
        {angemeldet ? (
          <div className={styles.konto}>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <MenuButton
                  appearance="subtle"
                  className={mergeClasses(styles.aufBlau, styles.kontoKnopf)}
                  menuIcon={{ className: styles.kontoPfeil }}
                  aria-label={`Konto von ${accountName}`}
                >
                  {/* Die Initialen als Schmucktext: der Name "Konto von <Name>" enthaelt den sichtbaren Namen, nicht "MB".
                      Ohne Buchstaben im Namen bildet Fluent selbst (dann das Personensymbol). */}
                  <Avatar
                    name={accountName}
                    size={28}
                    color="neutral"
                    initials={{ className: styles.kontoBild, ...(initialen === '' ? {} : { children: <span aria-hidden="true" className={schmuck.schmuck} {...{ [SCHMUCK_ATTRIBUT]: initialen }} /> }) }}
                  />
                  <span className={styles.kontoName}>{accountName}</span>
                </MenuButton>
              </MenuTrigger>
              <MenuPopover>
                <div className={styles.kontoZeile}>
                  <Body1 as="p" block>
                    {accountName}
                  </Body1>
                  {accountLogin !== undefined ? <Caption1>{accountLogin}</Caption1> : undefined}
                </div>
                <MenuDivider />
                <MenuList>
                  {darfKontakte ? (
                    <MenuItemLink href="/exporte" onClick={folgeVerweis}>
                      Meine Exporte
                    </MenuItemLink>
                  ) : undefined}
                  <MenuItemLink
                    href="/zugang"
                    onClick={folgeVerweis}
                  >
                    Dein Zugang
                  </MenuItemLink>
                  <MenuItem onClick={uebersichtAusMenue}>Tastenkürzel</MenuItem>
                  <MenuItem onClick={onSignOut}>Abmelden</MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </div>
        ) : undefined}
      </header>

      <div className={styles.rumpf}>
        {/* EINE Navigation fuer alle Breiten: schmal blendet der Menueknopf
            dieselbe Landmarke ein und aus, statt eine zweite einzuhaengen.

            KEINE Fluent-Tabs: Fluents Tab rendert role="tab", und hier wechselt
            die Adresse, nicht ein Bereich derselben Seite. Mittelklick, "in
            neuem Tab oeffnen" und "Adresse kopieren" blieben sonst auf der
            Strecke -- genau die Wege, die router.tsx offenhaelt. */}
        {/* Die abgedunkelte Flaeche unter dem schmalen Menue (M-04). Stumm und
            nicht fokussierbar: Schliessen per Tastatur geht ueber Esc und das X.
            mousedown verhindert den Fokuswechsel -- sonst schloesse der Blur das
            Menue vor dem Klick, die Flaeche waere weg und der Tipp traefe den
            Inhalt darunter. */}
        {angemeldet && screens.length > 0 && menueOffen ? (
          <div
            className={styles.scrim}
            aria-hidden="true"
            data-testid="menue-scrim"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setMenueOffen(false);
              document.getElementById('menueknopf')?.focus();
            }}
          />
        ) : undefined}
        {angemeldet && screens.length > 0 ? (
          <nav
            id="navigation"
            aria-label="Bereiche"
            className={mergeClasses(styles.nav, !menueOffen && styles.navZu)}
            onKeyDown={navTaste}
            onBlur={navVerlassen}
            // Ein Eintrag schliesst das Menue auch dann, wenn er auf die Seite
            // zeigt, auf der man schon ist. Dann aendert sich der Pfad nicht,
            // und der Fokus stuende auf einem ausgeblendeten Element: er geht
            // zurueck an den Menueknopf. Ein Klick mit Strg/Cmd, Umschalt, Alt
            // oder Mittelklick bleibt dem Browser, und das Menue bleibt offen.
            onClick={(event) => {
              const link = (event.target as HTMLElement).closest('a');
              if (link === null || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
              }
              setMenueOffen(false);
              if (link.getAttribute('href') === path) {
                document.getElementById('menueknopf')?.focus();
              }
            }}
          >
            <div className={styles.navInhalt}>
              <div className={styles.schliessenZeile}>
                <Button
                  appearance="subtle"
                  aria-label="Menü schließen"
                  className={styles.schliessenKnopf}
                  icon={<Symbol name="schliessen" groesse="1.25rem" />}
                  onClick={() => {
                    setMenueOffen(false);
                    document.getElementById('menueknopf')?.focus();
                  }}
                />
              </div>
              {NAV_GROUPS.map((gruppe) => {
                const eintraege = screens.filter((entry) => entry.navGroup === gruppe.id);
                if (eintraege.length === 0) {
                  return undefined;
                }
                return (
                  <div key={gruppe.id} className={styles.gruppe}>
                    {/* Die Arbeitsgruppe steht oben und braucht keinen sichtbaren
                        Kopf (v4); die Liste behaelt ihren Namen fuer Hilfsmittel. */}
                    <span
                      id={`navgruppe-${gruppe.id}`}
                      className={gruppe.id === 'arbeit' ? styles.nurLeser : styles.gruppeTitel}
                    >
                      {gruppe.label}
                    </span>
                    {/* role="list": Safari nimmt einer Liste mit list-style:none sonst
                        die Listensemantik. */}
                    <ul role="list" className={styles.liste} aria-labelledby={`navgruppe-${gruppe.id}`}>
                      {eintraege.map((entry) => (
                        <li key={entry.id}>
                          <AppLink
                            to={entry.path}
                            className={styles.navLink}
                            ariaCurrent={navigationCurrent(entry, screen, screens)}
                          >
                            {NAV_SYMBOL[entry.id] !== undefined ? <Symbol name={NAV_SYMBOL[entry.id]!} /> : undefined}
                            <span>{entry.navLabel}</span>
                          </AppLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </nav>
        ) : undefined}

        {/* tabIndex -1 macht den Bereich programmatisch fokussierbar, ohne ihn
            in die Tabreihenfolge aufzunehmen. id="hauptbereich" ist das Ziel
            des Skiplinks aus index.html. */}
        <main
          id="hauptbereich"
          className={mergeClasses(styles.main, !(angemeldet && screens.length > 0) && styles.mainOhneNav)}
          ref={main}
          tabIndex={-1}
        >
          {ueberschriftImInhalt && eigeneUeberschrift !== undefined ? undefined : (
            <Title3 as="h1" className={mergeClasses(styles.ueberschrift, ueberschriftImInhalt && styles.nurLeser)}>
              {eigeneUeberschrift ?? screen?.title ?? 'Seite nicht gefunden'}
            </Title3>
          )}
          <SeitenueberschriftKontext.Provider value={melden}>
            <UeberschriftImInhaltKontext.Provider value={setUeberschriftImInhalt}>
              <KontextbefehleKontext.Provider value={meldeKontext}>
                <SchnellzugriffKontext.Provider value={paletteVerfuegbar ? paletteOeffnen : undefined}>
                  <Fragment key={suchlauf}>{children}</Fragment>
                </SchnellzugriffKontext.Provider>
              </KontextbefehleKontext.Provider>
            </UeberschriftImInhaltKontext.Provider>
          </SeitenueberschriftKontext.Provider>
        </main>
      </div>

      {/* Nur eingehaengt, wenn sie gebraucht werden: Fluent setzt sonst
          Fokuswaechter in den Body, die axe als aria-hidden-focus meldet. */}
      {paletteOffen && paletteVerfuegbar ? (
        <Befehlspalette
          apiBaseUrl={apiBaseUrl}
          accessToken={accessToken}
          onReauthenticate={onReauthenticate}
          darfAnlegen={anlegenAngeboten}
          darfKontakte={darfKontakte}
          darfWiedervorlagen={darfWiedervorlagen}
          notizLabel={kontext.notiz?.label}
          onSchliessen={paletteSchliessen}
          onBefehl={befehlAusfuehren}
        />
      ) : undefined}
      {uebersichtOffen ? (
        <KuerzelUebersicht
          darfAnlegen={anlegenAngeboten}
          darfKontakte={darfKontakte}
          darfWiedervorlagen={darfWiedervorlagen}
          notizAngemeldet={kontext.notiz !== undefined}
          einzeltasten={einzeltasten}
          onEinzeltasten={setEinzeltasten}
          onSchliessen={uebersichtSchliessen}
        />
      ) : undefined}
    </div>
  );
}
